"use client";

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import {
  BarChart3,
  Building2,
  Calculator,
  Home,
  MapPin,
  Scale,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface NavSection {
  id: string;
  label: string;
  icon: React.ElementType;
}

interface PropertyNavigationV2Props {
  className?: string;
}

const sections: NavSection[] = [
  {
    id: "negotiation",
    label: "Negotiation",
    icon: Scale,
  },
  {
    id: "snapshot",
    label: "Property",
    icon: Home,
  },
  {
    id: "financial",
    label: "Financial",
    icon: Calculator,
  },
  {
    id: "market",
    label: "Market",
    icon: BarChart3,
  },
  {
    id: "location",
    label: "Location",
    icon: MapPin,
  },
  {
    id: "touring",
    label: "Tour",
    icon: Building2,
  },
];

export function PropertyNavigationV2({ className }: PropertyNavigationV2Props) {
  const [activeSection, setActiveSection] = useState<string | null>(
    "negotiation",
  );
  const [isSticky] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const navHeight = useRef<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll active tab into view when section changes
  useEffect(() => {
    if (activeSection && scrollContainerRef.current) {
      const activeButton = scrollContainerRef.current.querySelector(
        `[aria-controls="${activeSection}"]`,
      );
      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeSection]);

  useEffect(() => {
    // Set up intersection observer with dynamic rootMargin based on nav height
    const options = {
      rootMargin: `-${navHeight.current + 20}px 0px -70% 0px`,
      threshold: [0, 1],
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      }
    }, options);

    // Observe all sections
    for (const section of sections) {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    }

    return () => observer.disconnect();
  }, []);

  const handleNavClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = isSticky ? navHeight.current : 0;
      const targetPosition =
        element.getBoundingClientRect().top + window.scrollY - offset - 20;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });

      setActiveSection(sectionId);
    }
  };

  return (
    <div
      ref={navRef}
      className={cn(
        "bg-white border-b border-gray-200 transition-all duration-200",
        isSticky && "fixed left-0 right-0 z-40 shadow-sm top-[114px]", // 73px (navbar) + 41px (disclaimer)
        className,
      )}
      style={{
        marginBottom: isSticky ? `${navHeight.current}px` : undefined,
      }}
    >
      <div className="md:container md:max-w-7xl mx-0 md:mx-auto px-0 md:px-6">
        <div
          className="overflow-x-auto scrollbar-none"
          ref={scrollContainerRef}
        >
          <div
            className="flex gap-0 sm:gap-1 min-w-max pl-4 md:pl-0 relative"
            role="tablist"
          >
            {sections.map((section) => {
              const isActive = activeSection === section.id;
              const Icon = section.icon;
              return (
                <button
                  type="button"
                  role="tab"
                  key={section.id}
                  aria-selected={isActive}
                  aria-controls={section.id}
                  onClick={() => handleNavClick(section.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-4 py-3 text-sm transition-colors whitespace-nowrap relative",
                    isActive
                      ? "text-primary font-semibold"
                      : "text-gray-500 hover:text-gray-900",
                  )}
                >
                  <span className="flex items-center justify-center">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="font-semibold">{section.label}</span>
                  {isActive && (
                    <motion.div
                      layoutId="active-tab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                      initial={false}
                      animate={{
                        width: "100%",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 800,
                        damping: 45,
                        mass: 0.5,
                        duration: 0.2,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
